import { z } from 'zod';
import { OrderDirection } from '@/constants/orderDirection';
import type { PersonTargetsApiQuery } from '@/features/hr-core';
import { STARTED_TARGETS_FILTER_OPTIONS } from './-components/person/StartedTargetsFilter/types';

/**
 * API の category パラメータの型
 */
export type PersonTargetCategory = NonNullable<PersonTargetsApiQuery['category']>;

/**
 * API の orderBy パラメータの型（OpenAPI スキーマから派生）
 */
export type PersonSortColumn = NonNullable<PersonTargetsApiQuery['orderBy']>;

/**
 * UI テーブルカラム名から API ソートカラム名へのマッピング
 */
export const PersonColumnsToSortColumns: Record<string, PersonSortColumn> = {
  displayName: 'PERSON_NAME',
  workerTypeName: 'WORKER_TYPE',
  places: 'PLACE'
};

/**
 * API ソートカラム名から UI テーブルカラム名へのマッピング
 */
export const PersonSortColumnsToColumns: Record<PersonSortColumn, string> = {
  PERSON_NAME: 'displayName',
  WORKER_TYPE: 'workerTypeName',
  PLACE: 'places'
};

/**
 * URL 検索パラメータのスキーマ
 * Note: category と sortColumn は API 型と連動しているため、
 *       ここでは string として受け付け、API 呼び出し時に型チェックされる
 */
export const targetSelectionSearchSchema = z.object({
  page: z.number().optional().default(1),
  pageSize: z.number().optional().default(100),
  freeText: z.string().optional(),
  category: z.string().optional(),
  workerTypeIdIn: z.array(z.string()).optional(),
  employmentTypeIdIn: z.array(z.string()).optional(),
  contractorTypeIdIn: z.array(z.string()).optional(),
  placeIdIn: z.array(z.string()).optional(),
  leaveOfAbsenceTypeIdIn: z.array(z.string()).optional(),
  hasFamily: z.boolean().optional(),
  /** 当年入社（1/1〜12/31）に絞り込む。API 呼び出し時に joiningDateFrom/To へ変換する */
  joinedThisYear: z.boolean().optional(),
  /** 未依頼・アーカイブの対象者のみ表示する（YearEndAdjustment のみ） */
  excludeRequested: z.boolean().optional(),
  // 未指定はパッケージごとの既定値にフォールバックする（自己申告は IN_PROGRESS）。
  // 既定を解除した状態も URL で表現できるよう NONE を選択肢に含める。
  excludeStartedTargets: z.enum(STARTED_TARGETS_FILTER_OPTIONS).optional(),
  draftOnly: z.boolean().optional(),
  sortColumn: z.string().optional(),
  sortOrder: z.nativeEnum(OrderDirection).optional()
});
export type TargetSelectionSearch = z.infer<typeof targetSelectionSearchSchema>;